// ###### IMPORTS
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { ChevronRightIcon, ChevronDownIcon, PencilSquareIcon, CameraIcon } from '@heroicons/react/24/solid';
import { useAssetsData } from '../../../moduleServices/assets_data_context';
import { formatDate } from 'shared_items/formatConventions';

const CARD_BODY_MAX_H = 'max-h-[60vh]';

// ###### HELPERS
const formatValue = (v) => {
  if (v === null || v === undefined) return '—';
  const n = parseFloat(v);
  if (isNaN(n)) return '—';
  if (Math.abs(n) >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (Math.abs(n) >= 1_000) return `${(n / 1_000).toFixed(1)}k`;
  return n.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const sumSnapshotValue = (items) =>
  items.reduce((sum, it) => sum + (parseFloat(it.snapshot_value) || 0), 0);

const computeMarketValue = (item) => {
  const qty = parseFloat(item.snapshot_quantity);
  const mkt = parseFloat(item.market_unitary_value);
  if (!isNaN(qty) && !isNaN(mkt) && item.market_unitary_value != null) return qty * mkt;
  return null;
};

// ###### SUBCOMPONENTS
const LoadingSkeleton = ({ rows = 4 }) => (
  <div className="p-3 space-y-2">
    {Array.from({ length: rows }).map((_, i) => (
      <div key={i} className="animate-pulse h-8 bg-gray-100 rounded" />
    ))}
  </div>
);

const EmptyState = () => (
  <div className="flex items-center justify-center py-8 text-gray-400 text-sm">
    No snapshots recorded.
  </div>
);

const SnapshotRow = ({ snapshot, isExpanded, onToggle, cachedData, onEditSnapshot }) => {
  const items = cachedData?.items ?? [];
  const isItemsLoading = cachedData?.loading ?? false;
  const total = items.length > 0 ? sumSnapshotValue(items) : null;

  return (
    <div
      className="flex items-center gap-2 px-3 py-2.5 border-b border-gray-200 bg-gray-50 cursor-pointer select-none hover:bg-gray-100 transition-colors"
      onClick={() => onToggle(snapshot.id)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') onToggle(snapshot.id); }}
    >
      <span className="text-gray-400 shrink-0">
        {isExpanded
          ? <ChevronDownIcon className="h-4 w-4" />
          : <ChevronRightIcon className="h-4 w-4" />}
      </span>

      <span className="text-sm font-semibold text-gray-800">
        {formatDate(snapshot.snapshot_date, 'display')}
      </span>

      {snapshot.is_closing && (
        <span className="text-xs font-medium px-1.5 py-0.5 rounded-full bg-green-100 text-green-800 shrink-0">
          closing
        </span>
      )}

      {snapshot.notes && (
        <span className="text-xs text-gray-400 truncate min-w-0 flex-1" title={snapshot.notes}>
          {snapshot.notes}
        </span>
      )}

      <div className="ml-auto flex items-center gap-2 shrink-0">
        {onEditSnapshot && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onEditSnapshot(snapshot);
            }}
            className="p-1 rounded hover:bg-gray-200 text-gray-500 hover:text-green-800 transition-colors shrink-0"
            title="Edit snapshot"
          >
            <CameraIcon className="h-4 w-4" />
          </button>
        )}
        {isItemsLoading && (
          <div className="animate-spin rounded-full h-3 w-3 border-2 border-gray-300 border-t-green-700" />
        )}
        {!isItemsLoading && cachedData && (
          <span className="text-xs text-gray-400">({items.length})</span>
        )}
        {total !== null && (
          <span className="text-sm font-bold text-gray-700 tabular-nums">
            R$ {formatValue(total)}
          </span>
        )}
      </div>
    </div>
  );
};

const ItemRow = ({ item, onEditItem }) => {
  const marketTotal = computeMarketValue(item);

  return (
    <div className="flex items-center gap-2 pl-4 pr-3 py-1.5 border-b border-gray-100 hover:bg-gray-50/60 transition-colors">
      {onEditItem && (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onEditItem(item); }}
          className="p-1 rounded hover:bg-gray-200 text-gray-400 hover:text-gray-600 transition-colors shrink-0"
          title="Edit"
        >
          <PencilSquareIcon className="h-4 w-4" />
        </button>
      )}

      <span className="text-xs font-mono text-gray-400 bg-gray-100 px-1 py-0.5 rounded shrink-0 hidden sm:inline">
        {item.asset_category_code || '—'}
      </span>

      <div className="min-w-0 flex-1 flex items-center gap-1.5">
        <span className="text-xs font-mono text-gray-500 bg-gray-50 border border-gray-200 px-1 py-0.5 rounded shrink-0">
          {item.asset_code || '—'}
        </span>
        <span className="text-sm text-gray-800 truncate">
          {item.asset_name || '—'}
        </span>
      </div>

      <div className="flex items-center gap-3 shrink-0 text-right">
        {marketTotal !== null && (
          <span className="text-xs text-gray-400 tabular-nums">
            mkt R$ {formatValue(parseFloat(item.market_unitary_value))} | {formatValue(marketTotal)}
          </span>
        )}
        {item.snapshot_quantity != null && (
          <span className="text-xs text-gray-400 tabular-nums hidden sm:inline">
            qty {formatValue(item.snapshot_quantity)}
          </span>
        )}
        <span className="text-sm font-semibold text-gray-700 tabular-nums">
          R$ {formatValue(item.snapshot_value)}
        </span>
      </div>
    </div>
  );
};

// ###### MAIN COMPONENT
const CardSnapshotsDetailed = ({ onEditItem, snapshotItemUpdateRef, onEditSnapshot }) => {
  const { snapshots, snapshot_items: snapshotItems, loading, error, patchSnapshotItem } = useAssetsData();
  const [expandedIds, setExpandedIds] = useState(new Set());

  const sortedSnapshots = useMemo(
    () =>
      [...snapshots].sort((a, b) => (b.snapshot_date || '').localeCompare(a.snapshot_date || '')),
    [snapshots]
  );

  const itemsBySnapshotId = useMemo(() => {
    const map = {};
    (snapshotItems || []).forEach((i) => {
      const sid = i.snapshot;
      if (!map[sid]) map[sid] = [];
      map[sid].push(i);
    });
    Object.keys(map).forEach((sid) => {
      map[sid].sort((a, b) => {
        const catA = a.asset_category_code || '';
        const catB = b.asset_category_code || '';
        if (catA !== catB) return catA.localeCompare(catB);
        return (a.asset_code || '').localeCompare(b.asset_code || '');
      });
    });
    return map;
  }, [snapshotItems]);

  const updateItemInCache = useCallback(
    (updatedItem) => {
      patchSnapshotItem(updatedItem);
    },
    [patchSnapshotItem]
  );

  useEffect(() => {
    if (snapshotItemUpdateRef) snapshotItemUpdateRef.current = updateItemInCache;
  }, [snapshotItemUpdateRef, updateItemInCache]);

  const handleToggle = useCallback((snapshotId) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(snapshotId)) next.delete(snapshotId);
      else next.add(snapshotId);
      return next;
    });
  }, []);

  return (
    <div className="bg-white shadow rounded-xl overflow-hidden w-full">
      <div className="bg-gray-600 border-b border-gray-200 rounded-t-xl">
        <div className="px-4 py-2 flex justify-between items-center">
          <div className="flex items-center gap-2 text-white">
            <h2 className="text-lg font-semibold text-white">Snapshots</h2>
            {!loading && (
              <span className="text-xs text-white opacity-70">({sortedSnapshots.length})</span>
            )}
          </div>
        </div>
      </div>

      {error && (
        <div className="px-3 py-2 bg-red-50 border-b border-red-100 text-red-700 text-sm">
          {error}
        </div>
      )}

      <div className={`overflow-auto ${CARD_BODY_MAX_H}`}>
        {loading && <LoadingSkeleton rows={4} />}
        {!loading && sortedSnapshots.length === 0 && <EmptyState />}
        {!loading && sortedSnapshots.map((snapshot) => {
          const isExpanded = expandedIds.has(snapshot.id);
          const items = itemsBySnapshotId[snapshot.id] ?? [];
          const cachedData = { items, loading: false, error: null };
          return (
            <React.Fragment key={snapshot.id}>
              <SnapshotRow
                snapshot={snapshot}
                isExpanded={isExpanded}
                onToggle={handleToggle}
                cachedData={cachedData}
                onEditSnapshot={onEditSnapshot}
              />
              {isExpanded && (
                <>
                  {items.map((item) => (
                    <ItemRow key={item.id} item={item} onEditItem={onEditItem} />
                  ))}
                  {items.length === 0 && (
                    <div className="pl-8 pr-3 py-3 text-gray-400 text-xs border-b border-gray-100">
                      No items in this snapshot.
                    </div>
                  )}
                </>
              )}
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
};

export default CardSnapshotsDetailed;
